import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { SiteNavbar } from "@/components/SiteNavbar";
import { SiteFooter } from "@/components/SiteFooter";
import { PlanetHeroArt } from "@/components/PlanetHeroArt";
import { StatsStrip } from "@/components/StatsStrip";
import { CaptainsTeaser } from "@/components/CaptainsTeaser";
import { ArenaShowcase } from "@/components/ArenaShowcase";
import { CTABand } from "@/components/CTABand";
import { HERO, TICKETS } from "@/content/galaxy";

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Home() {

  return (
    <div className="relative min-h-screen overflow-x-clip">
      <SiteNavbar />

      {/* Hero */}
      <section className="hero-glow relative">
        <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-12 px-4 pb-16 pt-36 sm:px-6 lg:grid-cols-12 lg:gap-10 lg:px-8 lg:pb-24 lg:pt-44">
          {/* Left */}
          <div className="order-2 lg:order-1 lg:col-span-7">
            <motion.h1
              data-testid="hero-headline"
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={0}
              className="font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tight sm:text-6xl lg:text-7xl"
            >
              <span className="block whitespace-nowrap text-white">{HERO.headlineLine1}</span>
              <span className="block whitespace-nowrap text-gradient-brand">
                {HERO.headlineLine2}
              </span>
            </motion.h1>

            <motion.p
              data-testid="hero-location"
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={1}
              className="mt-4 flex items-center gap-1.5 font-mono-accent text-[11px] uppercase tracking-[0.28em] text-white/55 sm:text-xs"
            >
              2026 · NEURO.IO ARENA · WIEN
            </motion.p>

            <motion.p
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={2}
              className="mt-6 max-w-xl text-sm leading-relaxed text-white/70 sm:text-base"
            >
              {HERO.description}
            </motion.p>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={3}
              className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center"
            >
              <a
                href={TICKETS.url}
                data-testid="hero-tickets-button"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand-gradient px-7 py-3.5 text-sm font-semibold tracking-wide text-white glow-md transition-[filter,transform] duration-200 hover:brightness-110 hover:-translate-y-0.5 active:scale-[0.98]"
              >
                {HERO.primaryCta}
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </a>
            </motion.div>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={4}
            >
              <StatsStrip />
            </motion.div>

            <motion.p
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={5}
              className="mt-6 text-xs text-white/55 sm:text-sm"
            >
              {HERO.announcement}
            </motion.p>
          </div>

          {/* Right — hero art */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="order-1 flex justify-center lg:order-2 lg:col-span-5"
          >
            <PlanetHeroArt />
          </motion.div>
        </div>
      </section>

      <ArenaShowcase />

      <CaptainsTeaser />

      <CTABand />

      <SiteFooter />
    </div>
  );
}
